import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { getCoverages, updateCoverages } from '../services/api';
import './ListPage.css';
import './CoveragesPage.css';

// NER-1778 (FE): master de coberturas/convenios cross-industry por business.
// El PUT /coverages reemplaza TODO el master, así que la página mantiene la lista
// completa en estado local y persiste con un único "Guardar". `coverageLabel`
// reemplaza la palabra "Cobertura" en la UI según el rubro (obra social, aseguradora…).
const KEY_RE = /^[a-z][a-z0-9_]*$/;
const POLICIES = ['cover', 'partial', 'reject'];
const ADJUST_TYPES = ['fixed', 'percent'];
const DEFAULT_LABEL = 'Cobertura';

const EMPTY_COVERAGE = {
  key: '',
  name: '',
  isParticular: false,
  policy: 'cover',
  adjustmentType: 'fixed',
  adjustmentValue: 0,
};

function CoveragesPage() {
  const { t } = useTranslation();

  const [coverages, setCoverages] = useState([]);
  const [coverageLabel, setCoverageLabel] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // add / edit modal (editIndex === null → creando)
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(EMPTY_COVERAGE);
  const [formError, setFormError] = useState('');

  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Evita marcar dirty en la carga inicial (el setState del load dispararía el efecto).
  const hydrating = useRef(false);

  const label = coverageLabel || DEFAULT_LABEL;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    hydrating.current = true;
    try {
      const res = await getCoverages();
      setCoverages(res?.master?.coverages || []);
      setCoverageLabel(res?.master?.coverageLabel || '');
      setTemplates(res?.templates || []);
      setDirty(false);
    } catch (err) {
      console.error('Error loading coverages:', err);
      setError(t('coverages.loadError'));
    } finally {
      setLoading(false);
      hydrating.current = false;
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const markDirty = () => {
    if (!hydrating.current) {
      setDirty(true);
      setSaved(false);
    }
  };

  const openCreate = () => {
    setEditIndex(null);
    setForm(EMPTY_COVERAGE);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (index) => {
    const c = coverages[index];
    setEditIndex(index);
    setForm({
      key: c.key || '',
      name: c.name || '',
      isParticular: !!c.isParticular,
      policy: c.policy || 'cover',
      adjustmentType: c.adjustmentType || 'fixed',
      adjustmentValue: c.adjustmentValue == null ? 0 : c.adjustmentValue,
    });
    setFormError('');
    setShowForm(true);
  };

  const submitForm = (e) => {
    e.preventDefault();
    const key = form.key.trim();
    const name = form.name.trim();
    if (!KEY_RE.test(key)) {
      setFormError(t('coverages.keyInvalid'));
      return;
    }
    if (!name) {
      setFormError(t('coverages.nameRequired'));
      return;
    }
    // key única (ignorando el propio item al editar)
    const dupKey = coverages.some((c, i) => c.key === key && i !== editIndex);
    if (dupKey) {
      setFormError(t('coverages.keyDuplicate'));
      return;
    }
    // a lo sumo una isParticular
    if (form.isParticular) {
      const otherParticular = coverages.some((c, i) => c.isParticular && i !== editIndex);
      if (otherParticular) {
        setFormError(t('coverages.particularUnique'));
        return;
      }
    }
    const adjustmentValue = Number(form.adjustmentValue);
    if (Number.isNaN(adjustmentValue)) {
      setFormError(t('coverages.adjustmentInvalid'));
      return;
    }
    const entry = {
      key,
      name,
      isParticular: form.isParticular || undefined,
      policy: form.policy,
      adjustmentType: form.adjustmentType,
      adjustmentValue,
    };
    setCoverages((list) => {
      const next = [...list];
      if (editIndex === null) next.push(entry);
      else next[editIndex] = entry;
      return next;
    });
    markDirty();
    setShowForm(false);
  };

  const removeCoverage = (index) => {
    setCoverages((list) => list.filter((_, i) => i !== index));
    markDirty();
  };

  const applyTemplate = () => {
    const tpl = templates.find((tp) => tp.templateKey === selectedTemplate);
    if (!tpl) return;
    setCoverages(tpl.coverages || []);
    if (tpl.coverageLabel) setCoverageLabel(tpl.coverageLabel);
    markDirty();
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await updateCoverages({
        coverages,
        coverageLabel: coverageLabel.trim() || undefined,
      });
      setCoverages(res?.master?.coverages || coverages);
      setCoverageLabel(res?.master?.coverageLabel || coverageLabel);
      setDirty(false);
      setSaved(true);
    } catch (err) {
      console.error('Error saving coverages:', err);
      if (err?.response?.status === 403) {
        setError(t('coverages.forbidden'));
      } else if (err?.response?.status === 400) {
        setError(err?.response?.data?.message || t('coverages.saveError'));
      } else {
        setError(t('coverages.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Coberturas legacy/seed pueden venir sin `policy`. El backend trata
  // `policy === undefined` como 'cover' (admin-api.js putCoverages/quoteCoverage),
  // igual que el form (openEdit usa `c.policy || 'cover'`). Reflejamos ese default
  // acá para no renderear la key raw `coverages.policy_undefined` ni la clase
  // sin estilo `coverages-policy--undefined`.
  const policyOf = (c) => c.policy || 'cover';

  const describeAdjustment = (c) => {
    const v = c.adjustmentValue == null ? 0 : c.adjustmentValue;
    if (v === 0) return t('coverages.noAdjustment');
    if (c.adjustmentType === 'percent') {
      return `${v > 0 ? '+' : ''}${v}%`;
    }
    return `${v > 0 ? '+' : '-'}$${Math.abs(v)}`;
  };

  return (
    <div className="list-page">
      <div className="page-header">
        <h1>{t('coverages.title')}</h1>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} />
          {t('coverages.new', { label })}
        </button>
      </div>
      <p className="list-page-subtitle">{t('coverages.subtitle')}</p>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="card">
          <div className="loading">{t('common.loading')}</div>
        </div>
      ) : (
        <>
          <div className="card coverages-toolbar">
            <div className="form-group coverages-label-field">
              <label htmlFor="cov-label">{t('coverages.labelField')}</label>
              <input
                id="cov-label"
                type="text"
                value={coverageLabel}
                placeholder={DEFAULT_LABEL}
                maxLength={40}
                onChange={(e) => {
                  setCoverageLabel(e.target.value);
                  markDirty();
                }}
              />
              <span className="coverages-hint">{t('coverages.labelHint')}</span>
            </div>

            <div className="form-group coverages-template-field">
              <label htmlFor="cov-template">{t('coverages.applyTemplate')}</label>
              <div className="coverages-template-row">
                <select
                  id="cov-template"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  <option value="">{t('coverages.selectTemplate')}</option>
                  {templates.map((tp) => (
                    <option key={tp.templateKey} value={tp.templateKey}>
                      {tp.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={applyTemplate}
                  disabled={!selectedTemplate}
                >
                  {t('coverages.apply')}
                </button>
              </div>
              <span className="coverages-hint">{t('coverages.templateHint')}</span>
            </div>
          </div>

          <div className="card">
            {coverages.length === 0 ? (
              <div className="empty-state">{t('coverages.empty', { label })}</div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('coverages.colName')}</th>
                      <th>{t('coverages.colKey')}</th>
                      <th>{t('coverages.colPolicy')}</th>
                      <th>{t('coverages.colAdjustment')}</th>
                      <th className="col-actions">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coverages.map((c, i) => (
                      <tr key={c.key}>
                        <td>
                          {c.name}
                          {c.isParticular && (
                            <span className="list-tag coverages-particular-tag">
                              <Star size={12} />
                              {t('coverages.particular')}
                            </span>
                          )}
                        </td>
                        <td>
                          <code className="coverages-key">{c.key}</code>
                        </td>
                        <td>
                          <span className={`coverages-policy coverages-policy--${policyOf(c)}`}>
                            {t(`coverages.policy_${policyOf(c)}`)}
                          </span>
                        </td>
                        <td>{describeAdjustment(c)}</td>
                        <td className="col-actions">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => openEdit(i)}
                            aria-label={t('common.edit')}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => removeCoverage(i)}
                            aria-label={t('common.delete')}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="coverages-save-bar">
            {saved && <span className="coverages-saved">{t('coverages.saved')}</span>}
            {dirty && <span className="coverages-dirty">{t('coverages.unsaved')}</span>}
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={saving || !dirty}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </>
      )}

      {showForm && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowForm(false);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editIndex === null
                  ? t('coverages.newTitle', { label })
                  : t('coverages.editTitle', { label })}
              </h2>
            </div>
            <form onSubmit={submitForm}>
              <div className="modal-body">
                {formError && <div className="error-message">{formError}</div>}
                <div className="form-group">
                  <label htmlFor="cov-name">{t('coverages.colName')} *</label>
                  <input
                    id="cov-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={80}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cov-key">{t('coverages.colKey')} *</label>
                  <input
                    id="cov-key"
                    type="text"
                    value={form.key}
                    onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                    maxLength={40}
                    disabled={editIndex !== null}
                  />
                  <span className="coverages-hint">{t('coverages.keyHint')}</span>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cov-policy">{t('coverages.colPolicy')}</label>
                    <select
                      id="cov-policy"
                      value={form.policy}
                      onChange={(e) => setForm((f) => ({ ...f, policy: e.target.value }))}
                    >
                      {POLICIES.map((p) => (
                        <option key={p} value={p}>
                          {t(`coverages.policy_${p}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cov-adj-type">{t('coverages.adjustmentType')}</label>
                    <select
                      id="cov-adj-type"
                      value={form.adjustmentType}
                      onChange={(e) => setForm((f) => ({ ...f, adjustmentType: e.target.value }))}
                    >
                      {ADJUST_TYPES.map((a) => (
                        <option key={a} value={a}>
                          {t(`coverages.adjust_${a}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cov-adj-val">{t('coverages.adjustmentValue')}</label>
                    <input
                      id="cov-adj-val"
                      type="number"
                      step="any"
                      value={form.adjustmentValue}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, adjustmentValue: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="form-group coverages-checkbox-group">
                  <label className="coverages-checkbox">
                    <input
                      type="checkbox"
                      checked={form.isParticular}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, isParticular: e.target.checked }))
                      }
                    />
                    {t('coverages.isParticularLabel')}
                  </label>
                  <span className="coverages-hint">{t('coverages.isParticularHint')}</span>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowForm(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {editIndex === null ? t('coverages.addEntry') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoveragesPage;
